import { useEffect, useMemo, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  IconButton, InputAdornment, MenuItem, Select, Stack, Switch, Tab, Tabs,
  TextField, Tooltip, Typography,
} from '@mui/material'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AddIcon from '@mui/icons-material/Add'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import { AnimatePresence, motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, type ExportResource } from '@cmms/api-client'
import type { EmailSignature, Org, OrgPreferences, Role, RoleDetail, Permission, PermissionModule } from '@cmms/types'

import { primary } from '../../theme/palette'
import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useConfirm } from '../../components/ConfirmDialog'
import { useAuthStore } from '../../store/authStore'
import { useAppColorStore } from '../../store/appColorStore'
import { usePermissions } from '../../hooks/usePermissions'

import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import { AuditTrailTab } from './AuditTrailTab'

// HSE fork: SLA / Geofence / Subscription / Integrations / Inbound Email
// / Mobile Apps tab imports (both components and icons) removed.
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import { TrashTab } from './TrashTab'
import { InviteRequestsTab } from './InviteRequestsTab'
import { InvitationsTab } from './InvitationsTab'
import { OrganizationsTab } from './OrganizationsTab'
import { CustomersTab } from './CustomersTab'

import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined'

import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import { QrTab } from './QrTab'
import { SearchableSelect } from '../../components/SearchableSelect'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import { RiskCategoriesTab } from './RiskCategoriesTab'

type TabKey = 'profile' | 'roles' | 'general' | 'prefixes' | 'sla' | 'geofence' | 'customers' | 'sites' | 'signatures' | 'notifications' | 'subscription' | 'integrations' | 'inbound-email' | 'qr' | 'risk-categories' | 'audit' | 'downloads' | 'export' | 'trash' | 'invite-requests' | 'invitations' | 'organizations'

// ─── Constants ──────────────────────────────────────────────────────────────

const CURRENCIES = [
  { code: 'USD', symbol: '$',   label: 'US Dollar' },
  { code: 'EUR', symbol: '€',   label: 'Euro' },
  { code: 'GBP', symbol: '£',   label: 'British Pound' },
  { code: 'INR', symbol: '₹',   label: 'Indian Rupee' },
  { code: 'AUD', symbol: 'A$',  label: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$',  label: 'Canadian Dollar' },
  { code: 'JPY', symbol: '¥',   label: 'Japanese Yen' },
  { code: 'AED', symbol: 'د.إ',  label: 'UAE Dirham' },
  { code: 'SGD', symbol: 'S$',  label: 'Singapore Dollar' },
]

const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY — 04/17/2026' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY — 17/04/2026' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD — 2026-04-17' },
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY — 17 Apr 2026' },
]

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Toronto', 'Europe/London', 'Europe/Berlin', 'Europe/Paris',
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland',
]

// ─── Page ───────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const { can, isSuperAdmin } = usePermissions()
  const user = useAuthStore((s) => s.user)
  // Super admins without an org context need a tab that doesn't require
  // org-scoped permissions — invite-requests is the natural landing.
  const hasOrgContext = !!user?.org_id
  const defaultTab: TabKey = isSuperAdmin && !hasOrgContext
    ? 'invite-requests'
    : 'profile'

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  // HSE fork: Sites moved to /sites at the top level, so this page no
  // longer needs the path-based routing / siteId param / isSitesPath
  // dance the CMMS Settings page used.
  const VALID_TABS: TabKey[] = [
    'profile', 'roles', 'general', 'prefixes', 'customers',
    'notifications', 'qr', 'risk-categories', 'audit', 'export', 'trash',
    'invite-requests', 'invitations', 'organizations',
  ]
  const requestedTab = searchParams.get('tab') as TabKey | null
  const tab: TabKey = requestedTab && VALID_TABS.includes(requestedTab)
    ? requestedTab
    : defaultTab
  const setTab = (next: TabKey) => {
    const params = new URLSearchParams()
    params.set('tab', next)
    navigate(`/settings?${params.toString()}`, { replace: true })
  }

  const canManageRoles = can('settings:manage_roles')
  const canUpdateSettings = can('settings:update')
  const canExport = can('settings:export_data')
  const canViewAudit = can('audit:read')
  const canManageChecklists = can('checklists:create')

  const tabSx = {
    minHeight: 38,
    '& .MuiTab-root': {
      minHeight: 38, textTransform: 'none', fontSize: 13, fontWeight: 600,
      letterSpacing: '-0.005em', color: 'text.secondary', px: 0, mr: 3.5,
      '&.Mui-selected': { color: 'primary.main' },
      '&.Mui-disabled': { opacity: 0.4 },
    },
    '& .MuiTabs-indicator': { height: 2 },
  }

  const toolbar = (
    <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={tabSx}>
      <Tab value="profile" icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Profile" />
      <Tab value="roles" icon={<ShieldOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canManageRoles ? '' : "You don't have permission to manage roles"} arrow><span>Roles & Permissions</span></Tooltip>}
        disabled={!canManageRoles} />
      <Tab value="general" icon={<BusinessOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canUpdateSettings ? '' : "You don't have permission to change org settings"} arrow><span>General</span></Tooltip>}
        disabled={!canUpdateSettings} />
      <Tab value="prefixes" icon={<LabelOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canUpdateSettings ? '' : "You don't have permission to change prefix settings"} arrow><span>Module Prefixes</span></Tooltip>}
        disabled={!canUpdateSettings} />
      {/* HSE fork: SLA Policies, Geofence, Signatures, Subscription,
          Mobile Apps, Integrations, and Inbound Email tabs removed
          per customer request. Sites was promoted to a top-level nav
          item; visit /sites directly. */}
      <Tab value="customers" icon={<BusinessOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={can('customers:read') ? '' : "You don't have permission to view customers"} arrow><span>Customers</span></Tooltip>}
        disabled={!can('customers:read')} />
      <Tab value="notifications" icon={<NotificationsOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Notifications" />
      <Tab value="audit" icon={<HistoryOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canViewAudit ? '' : "You don't have permission to view audit logs"} arrow><span>Audit Trail</span></Tooltip>}
        disabled={!canViewAudit} />
      <Tab value="qr" icon={<QrCode2OutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canUpdateSettings ? '' : "You don't have permission to update settings"} arrow><span>QR Branding</span></Tooltip>}
        disabled={!canUpdateSettings} />
      <Tab value="risk-categories" icon={<ChecklistOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canManageChecklists ? '' : "You don't have permission to manage checklists"} arrow><span>Risk Categories</span></Tooltip>}
        disabled={!canManageChecklists} />
      <Tab value="export" icon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start"
        label={<Tooltip title={canExport ? '' : "You don't have permission to export data"} arrow><span>Data Export</span></Tooltip>}
        disabled={!canExport} />
      {isSuperAdmin && (
        <Tab value="organizations" icon={<DomainOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Organizations" />
      )}
      {isSuperAdmin && (
        <Tab value="invite-requests" icon={<MarkEmailReadOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Invite Requests" />
      )}
      {isSuperAdmin && (
        <Tab value="invitations" icon={<MarkEmailReadOutlinedIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Invitations" />
      )}
      {isSuperAdmin && (
        <Tab value="trash" icon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Deleted Items" />
      )}
    </Tabs>
  )

  return (
    <PageShell title="Settings" toolbar={toolbar} scrollContent={tab !== 'roles'}>
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
          style={tab === 'roles' ? { display: 'flex', flex: 1, minHeight: 0 } : {}}
        >
          {tab === 'profile'       && <ProfileTab />}
          {tab === 'roles'         && canManageRoles && <RolesTab />}
          {tab === 'general'       && canUpdateSettings && <GeneralTab />}
          {tab === 'prefixes'      && canUpdateSettings && <PrefixesTab />}
          {tab === 'customers'     && can('customers:read') && <CustomersTab />}
          {tab === 'notifications' && <NotificationsTab />}
          {tab === 'audit'         && canViewAudit && <AuditTrailTab />}
          {tab === 'qr'            && canUpdateSettings && <QrTab />}
          {tab === 'risk-categories' && canManageChecklists && <RiskCategoriesTab />}
          {tab === 'export'        && canExport && <ExportTab />}
          {tab === 'organizations'   && isSuperAdmin && <OrganizationsTab />}
          {tab === 'invite-requests' && isSuperAdmin && <InviteRequestsTab />}
          {tab === 'invitations'     && isSuperAdmin && <InvitationsTab />}
          {tab === 'trash'           && isSuperAdmin && <TrashTab />}
        </motion.div>
      </AnimatePresence>
    </PageShell>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// PROFILE TAB
// ═══════════════════════════════════════════════════════════════════════════

const APP_COLOR_PRESETS = [
  { color: '#0D9488', label: 'Teal' },
  { color: '#4F46E5', label: 'Indigo' },
  { color: '#2563EB', label: 'Blue' },
  { color: '#7C3AED', label: 'Violet' },
  { color: '#DB2777', label: 'Pink' },
  { color: '#DC2626', label: 'Red' },
  { color: '#EA580C', label: 'Orange' },
  { color: '#D97706', label: 'Amber' },
  { color: '#059669', label: 'Emerald' },
  { color: '#65A30D', label: 'Lime' },
  { color: '#0284C7', label: 'Sky' },
  { color: '#475569', label: 'Slate' },
]

function ProfileTab() {
  const qc = useQueryClient()
  const me = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const { primaryColor, setPrimaryColor } = useAppColorStore()

  const [name, setName]   = useState(me?.name ?? '')
  const [phone, setPhone] = useState(me?.phone ?? '')
  const [oldPw, setOldPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [customHex, setCustomHex] = useState(primaryColor ?? '')

  useEffect(() => { if (me) { setName(me.name); setPhone(me.phone ?? '') } }, [me])

  const updateM = useMutation({
    mutationFn: () => api.users.updateProfile({ name: name.trim(), phone: phone.trim() || undefined }),
    onSuccess: () => { toast.success('Profile updated'); api.auth.me().then(setUser) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  function handleColorPick(color: string) {
    setPrimaryColor(color)
    setCustomHex(color)
    // Persist to user preferences in DB
    api.users.updateProfile({ preferences: { primaryColor: color } }).catch(() => {})
  }

  function handleResetColor() {
    setPrimaryColor(null)
    setCustomHex('')
    api.users.updateProfile({ preferences: { primaryColor: null } }).catch(() => {})
    toast.success('Reset to default color')
  }

  function handleCustomHexApply() {
    const hex = customHex.trim()
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      handleColorPick(hex)
    } else {
      toast.error('Enter a valid hex color like #FF5722')
    }
  }

  const [showOldPw, setShowOldPw] = useState(false)
  const [showNewPw, setShowNewPw] = useState(false)

  const changePwM = useMutation({
    mutationFn: () => api.auth.changePassword({ current_password: oldPw, new_password: newPw }),
    onSuccess: () => {
      toast.success('Password changed')
      setOldPw(''); setNewPw('')
      setShowOldPw(false); setShowNewPw(false)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const initials = (me?.name ?? '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)

  const [avatarCacheBust, setAvatarCacheBust] = useState(Date.now())

  const uploadAvatarM = useMutation({
    mutationFn: (file: File) => api.users.uploadAvatar(file, (pct) => setUploadProgress(pct)),
    onSuccess: () => { toast.success('Avatar updated'); setUploadProgress(null); setAvatarCacheBust(Date.now()); api.auth.me().then(setUser) },
    onError: (e) => { setUploadProgress(null); toast.error(e instanceof ApiError ? e.message : 'Upload failed') },
  })

  const deleteAvatarM = useMutation({
    mutationFn: () => api.users.deleteAvatar(),
    onSuccess: () => { toast.success('Avatar removed'); setAvatarCacheBust(Date.now()); api.auth.me().then(setUser) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { toast.error('File must be under 5 MB'); return }
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) { toast.error('Only JPEG, PNG, WebP, or GIF allowed'); return }
    uploadAvatarM.mutate(file)
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
      {/* Left — profile info */}
      <Card>
        <CardHeader title="Your profile" subtitle="Manage your name, phone, and avatar." />
        <Stack spacing={3} sx={{ px: 3.5, pb: 3.5 }}>
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                src={me?.avatar_url ? `${me.avatar_url}?t=${avatarCacheBust}` : undefined}
                sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: 24, fontWeight: 700, cursor: 'pointer',
                  transition: 'opacity 150ms', '&:hover': { opacity: 0.8 } }}
                onClick={() => document.getElementById('avatar-upload')?.click()}
              >
                {initials}
              </Avatar>
              {uploadProgress !== null && (
                <CircularProgress variant="determinate" value={uploadProgress} size={78}
                  sx={{ position: 'absolute', top: -3, left: -3, color: 'primary.main' }} />
              )}
              <input id="avatar-upload" type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ display: 'none' }} onChange={handleAvatarChange} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.015em' }}>{me?.name}</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{me?.email}</Typography>
              <Chip label={me?.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())} size="small"
                sx={{ mt: 0.5, height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: 'rgba(13,148,136,0.10)', color: '#0D9488' }} />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" onClick={() => document.getElementById('avatar-upload')?.click()}
                  disabled={uploadAvatarM.isPending}
                  sx={{ fontSize: 11.5, fontWeight: 600, borderRadius: 99, px: 1.5, py: 0.25, textTransform: 'none' }}>
                  {uploadAvatarM.isPending ? 'Uploading...' : 'Change photo'}
                </Button>
                {me?.avatar_url && (
                  <Button size="small" onClick={() => deleteAvatarM.mutate()} disabled={deleteAvatarM.isPending}
                    sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', textTransform: 'none' }}>
                    Remove
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
          <Field label="Full name">
            <TextField value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth sx={inputSx} />
          </Field>
          <Field label="Phone (optional)">
            <TextField value={phone} onChange={(e) => setPhone(e.target.value)} size="small" fullWidth placeholder="+1 555-000-0000" sx={inputSx} />
          </Field>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={() => updateM.mutate()} disabled={updateM.isPending}
              startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
              sx={{ fontSize: 13, fontWeight: 600, px: 2.5 }}>
              {updateM.isPending ? 'Saving…' : 'Save profile'}
            </Button>
          </Box>
        </Stack>
      </Card>

      {/* Right — password */}
      <Card>
        <CardHeader title="Change password" subtitle="Use a strong, unique password." />
        <Stack spacing={3} sx={{ px: 3.5, pb: 3.5 }}>
          <Field label="Current password">
            <TextField type={showOldPw ? 'text' : 'password'} value={oldPw} onChange={(e) => setOldPw(e.target.value)}
              size="small" fullWidth sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowOldPw((v) => !v)}
                      aria-label={showOldPw ? 'Hide password' : 'Show password'}
                      edge="end" tabIndex={-1}>
                      {showOldPw
                        ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                        : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
          </Field>
          <Field label="New password">
            <TextField type={showNewPw ? 'text' : 'password'} value={newPw} onChange={(e) => setNewPw(e.target.value)}
              size="small" fullWidth sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowNewPw((v) => !v)}
                      aria-label={showNewPw ? 'Hide password' : 'Show password'}
                      edge="end" tabIndex={-1}>
                      {showNewPw
                        ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                        : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
          </Field>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => changePwM.mutate()} disabled={!oldPw || !newPw || changePwM.isPending}
              sx={{ fontSize: 13, fontWeight: 600, px: 2.5, color: 'primary.main', borderColor: 'divider' }}>
              {changePwM.isPending ? 'Changing…' : 'Change password'}
            </Button>
          </Box>
        </Stack>
      </Card>

      {/* App color — spans full width */}
      <Card sx={{ gridColumn: { lg: '1 / -1' } }}>
        <CardHeader title="App color" subtitle="Pick a primary color — it changes buttons, links, and accents across the entire app. Saved to your profile." />
        <Stack spacing={3} sx={{ px: 3.5, pb: 3.5 }}>
          {/* Preset swatches */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {APP_COLOR_PRESETS.map((p) => {
              const active = primaryColor === p.color
              return (
                <Tooltip key={p.color} title={p.label} arrow>
                  <Box
                    onClick={() => handleColorPick(p.color)}
                    sx={{
                      width: 40, height: 40, borderRadius: '50%', cursor: 'pointer',
                      border: '3px solid', borderColor: active ? p.color : 'transparent',
                      transition: 'border-color 120ms ease, transform 120ms ease',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      '&:hover': { transform: 'scale(1.1)' },
                    }}
                  >
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '50%', bgcolor: p.color,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {active && (
                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#fff' }} />
                      )}
                    </Box>
                  </Box>
                </Tooltip>
              )
            })}
          </Stack>

          {/* Custom hex input */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{
              width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
              bgcolor: /^#[0-9A-Fa-f]{6}$/.test(customHex) ? customHex : 'action.hover',
              border: '2px solid', borderColor: 'divider',
            }} />
            <TextField
              value={customHex}
              onChange={(e) => setCustomHex(e.target.value)}
              placeholder="#FF5722"
              size="small"
              sx={{ width: 140, '& .MuiOutlinedInput-root': { fontSize: 13, fontFamily: 'ui-monospace, monospace' } }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCustomHexApply() }}
            />
            <Button size="small" onClick={handleCustomHexApply}
              sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main' }}>
              Apply
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={handleResetColor}
              sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary' }}>
              Reset to default
            </Button>
          </Stack>
        </Stack>
      </Card>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// ROLES & PERMISSIONS TAB
// ═══════════════════════════════════════════════════════════════════════════

function RolesTab() {
  const qc = useQueryClient()
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: () => api.roles.list() })
  const roles = Array.isArray(rolesQ.data) ? rolesQ.data : []

  // Auto-select first role
  if (!selectedRoleId && roles.length > 0) setSelectedRoleId(roles[0]!.id)

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', mb: 4 }}>
      {/* Role list */}
      <Box sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, borderRight: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Roles</Typography>
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setCreateOpen(true)}
            sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>New</Button>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1, scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
          '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
          '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>
          {rolesQ.isLoading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
          {roles.map((r) => {
            const sel = r.id === selectedRoleId
            return (
              <Stack key={r.id} direction="row" alignItems="center" spacing={1.25} onClick={() => setSelectedRoleId(r.id)}
                sx={{
                  position: 'relative', px: 2.5, py: 1.25, cursor: 'pointer',
                  transition: 'background-color 120ms ease',
                  bgcolor: sel ? primary.selected : 'transparent',
                  '&:hover': { bgcolor: sel ? primary.selectedHover : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)' },
                  '&::before': sel ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main' } : undefined,
                }}>
                <ShieldOutlinedIcon sx={{ fontSize: 16, color: sel ? 'primary.main' : 'text.secondary' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: sel ? 600 : 500 }} noWrap>{r.name}</Typography>
                    {r.is_system && <LockOutlinedIcon sx={{ fontSize: 11, color: 'text.secondary', opacity: 0.6 }} />}
                  </Stack>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    {r.user_count ?? 0} user{(r.user_count ?? 0) === 1 ? '' : 's'} · {r.permission_count ?? 0} permission{(r.permission_count ?? 0) === 1 ? '' : 's'}
                  </Typography>
                </Box>
              </Stack>
            )
          })}
        </Box>
      </Box>

      {/* Permission editor */}
      {selectedRoleId && <RolePermissionEditor roleId={selectedRoleId} />}

      <CreateRoleDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </Box>
  )
}

function RolePermissionEditor({ roleId }: { roleId: string }) {
  const qc = useQueryClient()
  const roleQ = useQuery({ queryKey: ['role-detail', roleId], queryFn: () => api.roles.get(roleId) })
  const permsQ = useQuery({ queryKey: ['permissions'], queryFn: () => api.permissions.list(), staleTime: 10 * 60 * 1000 })
  const modulesQ = useQuery({ queryKey: ['permission-modules'], queryFn: () => api.permissions.listModules(), staleTime: 10 * 60 * 1000 })

  const role = roleQ.data
  const allPerms = permsQ.data ?? []
  const modules = modulesQ.data ?? []

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (role?.permissions) {
      setSelected(new Set(role.permissions.map((p) => p.id)))
      setDirty(false)
    }
  }, [role])

  // HSE fork: hide permissions for modules the app no longer ships.
  // These rows still exist in the DB (seeded from CMMS migrations)
  // but exposing them would let an admin toggle checkboxes with no
  // corresponding UI — pure noise. The names match `permissions.module`
  // as seeded by the backend; anything not in this set stays visible.
  const strippedModules = useMemo(() => new Set<string>([
    'work_orders', 'tasks', 'assets', 'asset_categories', 'categories',
    'locations', 'inventory', 'parts', 'vendors',
    'workflows', 'automations', 'automation',
    'checklists', 'sla', 'geofence',
    'integrations', 'inbound_email', 'reports', 'signatures',
    'subscription', 'downloads', 'trash',
  ]), [])

  // Individual permission keys that live under a *kept* module but
  // point at surfaces we've already stripped (e.g. work-order-scoped
  // permissions on the requests module).
  const strippedPermNames = useMemo(() => new Set<string>([
    'work_orders:manage_geofence',
  ]), [])

  const visiblePerms = useMemo(
    () => allPerms.filter((p) =>
      !strippedModules.has(p.module) && !strippedPermNames.has(p.name),
    ),
    [allPerms, strippedModules, strippedPermNames],
  )

  const grouped = useMemo(() => {
    const map = new Map<string, Permission[]>()
    visiblePerms.forEach((p) => {
      const arr = map.get(p.module) ?? []
      arr.push(p)
      map.set(p.module, arr)
    })
    return map
  }, [visiblePerms])

  function toggle(permId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(permId)) next.delete(permId)
      else next.add(permId)
      return next
    })
    setDirty(true)
  }

  function toggleModule(module: string) {
    const modulePerms = grouped.get(module) ?? []
    const allSelected = modulePerms.every((p) => selected.has(p.id))
    setSelected((prev) => {
      const next = new Set(prev)
      modulePerms.forEach((p) => { if (allSelected) next.delete(p.id); else next.add(p.id) })
      return next
    })
    setDirty(true)
  }

  const saveM = useMutation({
    mutationFn: () => api.roles.update(roleId, { permission_ids: Array.from(selected) }),
    onSuccess: () => {
      toast.success('Permissions saved')
      qc.invalidateQueries({ queryKey: ['role-detail', roleId] })
      qc.invalidateQueries({ queryKey: ['roles'] })
      setDirty(false)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  const deleteM = useMutation({
    mutationFn: () => api.roles.remove(roleId),
    onSuccess: () => {
      toast.success('Role deleted')
      qc.invalidateQueries({ queryKey: ['roles'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  const duplicateM = useMutation({
    mutationFn: () => api.roles.duplicate(roleId),
    onSuccess: (data) => {
      toast.success(`Duplicated as "${data.name}"`)
      qc.invalidateQueries({ queryKey: ['roles'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to duplicate'),
  })

  const [renameOpen, setRenameOpen] = useState(false)
  const [renameName, setRenameName] = useState('')
  const [renameDesc, setRenameDesc] = useState('')

  const renameM = useMutation({
    mutationFn: () => api.roles.update(roleId, { name: renameName.trim(), description: renameDesc.trim() || undefined }),
    onSuccess: () => {
      toast.success('Role renamed')
      qc.invalidateQueries({ queryKey: ['role-detail', roleId] })
      qc.invalidateQueries({ queryKey: ['roles'] })
      setRenameOpen(false)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to rename'),
  })

  const confirmDlg = useConfirm()

  if (roleQ.isLoading || permsQ.isLoading) {
    return <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
  }
  if (!role) return null

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Role header */}
      <Box sx={{ px: 4, pt: 3, pb: 2.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>{role.name}</Typography>
              {role.is_system && <Chip label="System" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: 'action.hover', color: 'text.secondary' }} />}
            </Stack>
            {role.description && <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.35 }}>{role.description}</Typography>}
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
              {role.user_count ?? 0} user{(role.user_count ?? 0) === 1 ? '' : 's'} · {selected.size} / {visiblePerms.length} permissions
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75}>
            {!role.is_system && (
              <Tooltip title="Rename role" arrow>
                <IconButton size="small" onClick={() => { setRenameName(role.name); setRenameDesc(role.description ?? ''); setRenameOpen(true) }}
                  sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Duplicate role" arrow>
              <IconButton size="small" onClick={() => duplicateM.mutate()}
                disabled={duplicateM.isPending}
                sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
                <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {!role.is_system && (
              <Tooltip title="Delete role" arrow>
                <IconButton size="small" onClick={() => { confirmDlg({ title: `Delete "${role.name}"?`, message: "Users assigned to this role will need a new one.", confirmLabel: "Delete", variant: "danger" }).then((ok) => ok && deleteM.mutate()) }}
                  disabled={deleteM.isPending}
                  sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, '&:hover': { color: 'error.main' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Permissions grid */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3,
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 2.5 }}>
          {Array.from(grouped.entries()).map(([module, perms]) => {
            const allChecked = perms.every((p) => selected.has(p.id))
            const someChecked = perms.some((p) => selected.has(p.id)) && !allChecked
            return (
              <Box key={module} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}
                  sx={{ px: 2.5, py: 1.75, bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Checkbox
                    size="small"
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={() => toggleModule(module)}
                    sx={{ p: 0 }}
                  />
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                    {module.replace(/_/g, ' ')}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                    {perms.filter((p) => selected.has(p.id)).length}/{perms.length}
                  </Typography>
                </Stack>
                <Stack spacing={0} sx={{ px: 1.5, py: 0.75 }}>
                  {perms.map((p) => (
                    <FormControlLabel
                      key={p.id}
                      control={<Checkbox size="small" checked={selected.has(p.id)} onChange={() => toggle(p.id)} sx={{ p: 0.5 }} />}
                      label={
                        <Box sx={{ ml: 0.75 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 500, letterSpacing: '-0.005em' }}>
                            {p.name.split(':')[1]?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) ?? p.name}
                          </Typography>
                          {p.description && (
                            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.35 }}>{p.description}</Typography>
                          )}
                        </Box>
                      }
                      sx={{ mx: 0, py: 0.75, alignItems: 'flex-start' }}
                    />
                  ))}
                </Stack>
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Save bar */}
      <Box sx={{ flexShrink: 0, px: 4, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 12.5, color: dirty ? 'primary.main' : 'text.secondary', fontWeight: 600 }}>
          {dirty ? 'You have unsaved changes' : 'All changes saved'}
        </Typography>
        <Button variant="contained" startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={() => saveM.mutate()} disabled={!dirty || saveM.isPending}
          sx={{ fontSize: 13, fontWeight: 600, px: 3 }}>
          {saveM.isPending ? 'Saving…' : 'Save permissions'}
        </Button>
      </Box>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Rename role</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" required value={renameName} onChange={(e) => setRenameName(e.target.value)}
              size="small" fullWidth autoFocus />
            <TextField label="Description" value={renameDesc} onChange={(e) => setRenameDesc(e.target.value)}
              size="small" fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRenameOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" disabled={!renameName.trim() || renameM.isPending}
            onClick={() => renameM.mutate()}>
            {renameM.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function CreateRoleDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')

  const createM = useMutation({
    mutationFn: () => api.roles.create({ name: name.trim(), description: desc.trim() || undefined }),
    onSuccess: () => { toast.success('Role created'); qc.invalidateQueries({ queryKey: ['roles'] }); setName(''); setDesc(''); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Create role</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Role name" required value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus sx={inputSx} />
          <TextField label="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} size="small" fullWidth multiline minRows={2} sx={inputSx} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={() => createM.mutate()} disabled={!name.trim() || createM.isPending} sx={{ fontSize: 13, fontWeight: 600 }}>
          {createM.isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// GENERAL TAB
// ═══════════════════════════════════════════════════════════════════════════

function GeneralTab() {
  const qc = useQueryClient()
  const orgQ = useQuery({ queryKey: ['org'], queryFn: () => api.orgs.getCurrent() })
  const org = orgQ.data

  const [name, setName] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY')
  const [timeFormat, setTimeFormat] = useState('12h')
  const [timezone, setTimezone] = useState('')

  useEffect(() => {
    if (!org) return
    setName(org.name)
    setCurrency(org.preferences?.currency ?? 'USD')
    setDateFormat(org.preferences?.dateFormat ?? 'MM/DD/YYYY')
    setTimeFormat(org.preferences?.timeFormat ?? '12h')
    setTimezone(org.preferences?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone)
  }, [org])

  const updateM = useMutation({
    mutationFn: () => {
      const cm = CURRENCIES.find((c) => c.code === currency)
      return api.orgs.update({
        name: name.trim(),
        preferences: { currency, currencySymbol: cm?.symbol ?? '$', dateFormat, timeFormat, timezone, language: 'en' },
      })
    },
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries({ queryKey: ['org'] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  if (orgQ.isLoading) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
      <Card>
        <CardHeader title="Organization" subtitle="Your workspace identity." />
        <Stack spacing={3} sx={{ px: 3.5, pb: 3.5 }}>
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Box sx={{ width: 56, height: 56, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: (t) => `${t.palette.primary.main}18`, color: 'primary.main', flexShrink: 0 }}>
              <BusinessOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em' }}>{org?.name}</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontFamily: 'ui-monospace, monospace' }}>{org?.slug}</Typography>
            </Box>
          </Stack>
          <Field label="Organization name">
            <TextField value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth sx={inputSx} />
          </Field>
        </Stack>
      </Card>

      <Card>
        <CardHeader title="Regional preferences" subtitle="Applied across all screens." />
        <Stack spacing={3} sx={{ px: 3.5, pb: 3.5 }}>
          <Field label="Currency">
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)} size="small" fullWidth sx={{ fontSize: 13.5 }}>
              {CURRENCIES.map((c) => <MenuItem key={c.code} value={c.code} sx={{ fontSize: 13 }}>{c.symbol} {c.label} ({c.code})</MenuItem>)}
            </Select>
          </Field>
          <Field label="Date format">
            <Select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} size="small" fullWidth sx={{ fontSize: 13.5 }}>
              {DATE_FORMATS.map((f) => <MenuItem key={f.value} value={f.value} sx={{ fontSize: 13 }}>{f.label}</MenuItem>)}
            </Select>
          </Field>
          <Field label="Time format">
            <Select value={timeFormat} onChange={(e) => setTimeFormat(e.target.value)} size="small" fullWidth sx={{ fontSize: 13.5 }}>
              <MenuItem value="12h" sx={{ fontSize: 13 }}>12-hour (1:30 PM)</MenuItem>
              <MenuItem value="24h" sx={{ fontSize: 13 }}>24-hour (13:30)</MenuItem>
            </Select>
          </Field>
          <Field label="Timezone">
            <SearchableSelect
              value={timezone}
              onChange={setTimezone}
              options={TIMEZONES.map((tz) => ({ value: tz, label: tz.replace(/_/g, ' ') }))}
              getValue={(o) => o.value}
              getLabel={(o) => o.label}
              placeholder="Select a timezone"
            />
          </Field>
        </Stack>
      </Card>

      <Box sx={{ gridColumn: { lg: '1 / -1' }, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={() => updateM.mutate()} disabled={updateM.isPending}
          sx={{ fontSize: 13, fontWeight: 600, px: 3 }}>
          {updateM.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// MODULE PREFIXES TAB
// ═══════════════════════════════════════════════════════════════════════════

// HSE fork: only the modules HSE ships stay here — the rest were
// stripped and their prefix knobs would confuse admins.
const PREFIX_MODULES = [
  { key: 'request', label: 'Reports', icon: '📋', example: 'REQ', desc: 'Used in report codes like REQ-00001' },
] as const

function PrefixesTab() {
  const qc = useQueryClient()
  const orgQ = useQuery({ queryKey: ['org'], queryFn: () => api.orgs.getCurrent() })
  const org = orgQ.data

  const [prefixes, setPrefixes] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!org) return
    const saved = (org.preferences as any)?.modulePrefixes ?? {}
    const merged: Record<string, string> = {}
    for (const m of PREFIX_MODULES) merged[m.key] = saved[m.key] ?? m.example
    setPrefixes(merged)
  }, [org])

  const updateM = useMutation({
    mutationFn: () =>
      api.orgs.update({
        preferences: { ...(org?.preferences ?? {}), modulePrefixes: prefixes } as any,
      }),
    onSuccess: () => { toast.success('Prefixes saved'); qc.invalidateQueries({ queryKey: ['org'] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  if (orgQ.isLoading) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Card>
        <CardHeader title="Module prefixes" subtitle="Configure the prefix tag used when auto-generating codes for each module. Changes apply to newly created items only." />
        <Stack spacing={0} sx={{ px: 3.5, pb: 2 }}>
          {PREFIX_MODULES.map((m) => {
            const val = prefixes[m.key] ?? m.example
            const preview = `${val}-00001`
            return (
              <Stack key={m.key} direction="row" alignItems="center" spacing={2.5}
                sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                <Typography sx={{ fontSize: 22, width: 36, textAlign: 'center', flexShrink: 0 }}>{m.icon}</Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{m.label}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{m.desc}</Typography>
                </Box>
                <TextField
                  value={val}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)
                    setPrefixes((p) => ({ ...p, [m.key]: v }))
                  }}
                  size="small"
                  inputProps={{ style: { textAlign: 'center', fontWeight: 700, fontFamily: 'ui-monospace, monospace', letterSpacing: '0.05em' } }}
                  sx={{ width: 90, '& .MuiOutlinedInput-root': { fontSize: 14 } }}
                />
                <Chip
                  label={preview}
                  size="small"
                  sx={{
                    height: 26, fontSize: 12, fontWeight: 600, fontFamily: 'ui-monospace, monospace',
                    bgcolor: (t) => `${t.palette.primary.main}10`, color: 'primary.main',
                    minWidth: 110, justifyContent: 'center',
                    '& .MuiChip-label': { px: 1.5 },
                  }}
                />
              </Stack>
            )
          })}
        </Stack>
      </Card>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={() => updateM.mutate()} disabled={updateM.isPending}
          sx={{ fontSize: 13, fontWeight: 600, px: 3 }}>
          {updateM.isPending ? 'Saving…' : 'Save prefixes'}
        </Button>
      </Box>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// NOTIFICATIONS TAB
// ═══════════════════════════════════════════════════════════════════════════

// HSE fork: notification prefs pared back to just the HSE report
// lifecycle + chat. The stripped events (work orders, SLA, assets,
// inventory, automations) don't fire in this build.
const NOTIF_PREFS = [
  { label: 'New report submitted',     desc: 'When a public risk report lands on a site your team owns.',          key: 'report_submitted',    def: true },
  { label: 'Teammate acknowledged',    desc: 'When another roster member acknowledges a report you’re on.',     key: 'report_partial_ack',  def: true },
  { label: 'Report fully acknowledged', desc: 'When the last person on the roster closes a report out.',           key: 'report_full_ack',     def: true },
  { label: 'Chat messages',            desc: 'When someone sends you a direct or team message.',                   key: 'chat_message',        def: true },
]

function NotificationsTab() {
  const qc = useQueryClient()
  const [prefs, setPrefs] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    NOTIF_PREFS.forEach((n) => { init[n.key] = n.def })
    return init
  })
  const [loaded, setLoaded] = useState(false)
  const [dirty, setDirty] = useState(false)

  // Load saved preferences
  const prefsQ = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => api.notifications.getPreferences(),
  })

  useEffect(() => {
    if (prefsQ.data && !loaded) {
      const saved = prefsQ.data as Record<string, boolean>
      const merged: Record<string, boolean> = {}
      NOTIF_PREFS.forEach((n) => { merged[n.key] = saved[n.key] ?? n.def })
      setPrefs(merged)
      setLoaded(true)
    }
  }, [prefsQ.data, loaded])

  const saveM = useMutation({
    mutationFn: () => api.notifications.updatePreferences(prefs),
    onSuccess: () => {
      toast.success('Notification preferences saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['notification-preferences'] })
    },
    onError: (e: any) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  function toggle(key: string) {
    setPrefs((p) => ({ ...p, [key]: !p[key] }))
    setDirty(true)
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      {/* HSE fork: org-level knob for the re-notification cadence. */}
      <ReportReminderCard />

      <Card>
        <CardHeader title="Your notification preferences" subtitle="Choose which events send you in-app + push notifications." />
        <Stack spacing={0} sx={{ px: 3.5, pb: 3.5 }}>
          {NOTIF_PREFS.map((n, i) => (
            <Stack key={n.key} direction="row" alignItems="center" spacing={2}
              sx={{ py: 2, borderBottom: i < NOTIF_PREFS.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.005em' }}>{n.label}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.15 }}>{n.desc}</Typography>
              </Box>
              <Switch checked={prefs[n.key] ?? false} onChange={() => toggle(n.key)} size="small" />
            </Stack>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 2.5 }}>
            <Typography sx={{ fontSize: 12.5, color: dirty ? 'primary.main' : 'text.secondary', fontWeight: 600 }}>
              {dirty ? 'You have unsaved changes' : 'All changes saved'}
            </Typography>
            <Button variant="contained" startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
              disabled={!dirty || saveM.isPending} onClick={() => saveM.mutate()}
              sx={{ fontSize: 13, fontWeight: 600, px: 3 }}>
              {saveM.isPending ? 'Saving...' : 'Save preferences'}
            </Button>
          </Box>
        </Stack>
      </Card>
    </Stack>
  )
}

// HSE fork: org-scoped card that owns the "how often do we re-notify
// unacknowledged reports?" setting. Storage lives in
// `orgs.preferences.report_reminder_minutes`; the scheduler tick reads
// this every minute so a save is picked up on the next tick. Legacy
// `report_reminder_hours` is still honoured server-side as a
// fallback so old orgs don't lose their setting on read.
function ReportReminderCard() {
  const qc = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can('settings:update')
  const orgQ = useQuery({ queryKey: ['org'], queryFn: () => api.orgs.getCurrent() })

  // Prefer the minutes field. Fall back to hours * 60 for orgs that
  // still have only the legacy value. Default 2 hours.
  const prefs = (orgQ.data?.preferences as {
    report_reminder_minutes?: number
    report_reminder_hours?: number
  } | undefined) ?? {}
  const currentMinutes =
    typeof prefs.report_reminder_minutes === 'number'
      ? prefs.report_reminder_minutes
      : typeof prefs.report_reminder_hours === 'number'
      ? prefs.report_reminder_hours * 60
      : 120
  const [minutes, setMinutes] = useState<number>(currentMinutes)

  useEffect(() => { setMinutes(currentMinutes) }, [currentMinutes])

  const saveM = useMutation({
    mutationFn: () => api.orgs.update({
      preferences: {
        ...(orgQ.data?.preferences ?? {}),
        report_reminder_minutes: minutes,
        // Keep the legacy hours field in sync for anything that still
        // reads it. Rounded up so a "test" 5-minute value doesn't
        // truncate to 0 hours.
        report_reminder_hours: Math.max(1, Math.round(minutes / 60)),
      } as OrgPreferences,
    }),
    onSuccess: () => {
      toast.success('Reminder frequency saved')
      qc.invalidateQueries({ queryKey: ['org'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to save'),
  })

  const dirty = minutes !== currentMinutes
  // Sub-hour options for testing, then the practical range.
  const OPTIONS: { minutes: number; label: string; testing?: boolean }[] = [
    { minutes: 2,     label: 'Every 2 minutes',  testing: true },
    { minutes: 5,     label: 'Every 5 minutes',  testing: true },
    { minutes: 15,    label: 'Every 15 minutes', testing: true },
    { minutes: 30,    label: 'Every 30 minutes' },
    { minutes: 60,    label: 'Every hour' },
    { minutes: 120,   label: 'Every 2 hours' },
    { minutes: 240,   label: 'Every 4 hours' },
    { minutes: 480,   label: 'Every 8 hours' },
    { minutes: 720,   label: 'Every 12 hours' },
    { minutes: 1440,  label: 'Every 24 hours' },
  ]

  const currentLabel = OPTIONS.find((o) => o.minutes === currentMinutes)?.label
    ?? (currentMinutes < 60
      ? `every ${currentMinutes} minutes`
      : currentMinutes === 60
      ? 'every hour'
      : `every ${(currentMinutes / 60).toFixed(currentMinutes % 60 === 0 ? 0 : 1)} hours`)

  return (
    <Card>
      <CardHeader
        title="Report re-notification frequency"
        subtitle="How often HSE re-pushes an unacknowledged report to the roster members who still haven't checked in."
      />
      <Box sx={{ px: 3.5, pb: 3.5 }}>
        <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {OPTIONS.map((opt) => {
            const active = minutes === opt.minutes
            return (
              <Chip key={opt.minutes}
                label={opt.label}
                onClick={canEdit ? () => setMinutes(opt.minutes) : undefined}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  height: 32, fontSize: 12.5, fontWeight: 700, borderRadius: 2,
                  bgcolor: active
                    ? (opt.testing ? '#D97706' : 'primary.main')
                    : 'transparent',
                  color: active ? '#FFFFFF' : (opt.testing ? '#D97706' : 'text.primary'),
                  borderColor: active
                    ? (opt.testing ? '#D97706' : 'primary.main')
                    : (opt.testing ? 'rgba(217,119,6,0.5)' : 'divider'),
                  cursor: canEdit ? 'pointer' : 'default',
                  '&:hover': canEdit ? {
                    bgcolor: active
                      ? (opt.testing ? '#B45309' : 'primary.dark')
                      : 'action.hover',
                  } : {},
                }}
              />
            )
          })}
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.55, mb: 2.5 }}>
          The scheduler runs every minute. Once a report has been sitting unacknowledged for the interval above, HSE fires a fresh web-push notification to every roster member who still hasn’t acknowledged. The reminder repeats on the same cadence until they acknowledge or the report is fully closed out. <strong>The amber options (≤ 15 min) are meant for testing</strong> — leave those on in production and your on-call phones will vibrate all night.
        </Typography>
        {canEdit ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: 12.5, color: dirty ? 'primary.main' : 'text.secondary', fontWeight: 600 }}>
              {dirty ? 'You have unsaved changes' : 'Currently ' + currentLabel}
            </Typography>
            <Button variant="contained" startIcon={<SaveOutlinedIcon sx={{ fontSize: 15 }} />}
              disabled={!dirty || saveM.isPending || orgQ.isLoading} onClick={() => saveM.mutate()}
              sx={{ fontSize: 13, fontWeight: 600, px: 3 }}>
              {saveM.isPending ? 'Saving…' : 'Save frequency'}
            </Button>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>
            Only admins can change the reminder frequency.
          </Typography>
        )}
      </Box>
    </Card>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// DATA EXPORT TAB
// ═══════════════════════════════════════════════════════════════════════════

// HSE fork: Work Orders, Assets, Locations, and Inventory exports
// dropped with the modules they exported. Sites + Reports added
// because they're the HSE product's own first-class surfaces.
const EXPORT_OPTIONS: { label: string; desc: string; icon: React.ElementType; resource: ExportResource }[] = [
  { label: 'Reports', desc: 'Every risk report with site, team, and per-roster ack progress.', icon: DescriptionOutlinedIcon, resource: 'reports' },
  { label: 'Sites',   desc: 'Every site with address, coords, entity meta, and linked team.',  icon: LocationOnOutlinedIcon,  resource: 'sites' },
  { label: 'Users',   desc: 'User roster with roles and site access.',                         icon: PersonOutlineIcon,       resource: 'users' },
  { label: 'Teams',   desc: 'Team structure and membership.',                                  icon: ShieldOutlinedIcon,      resource: 'teams' },
]

function ExportTab() {
  const [exporting, setExporting] = useState<string | null>(null)

  async function handleExport(label: string, resource: ExportResource) {
    setExporting(label)
    try {
      const { blob, filename } = await api.exports.download(resource)
      // Standard browser-download pattern: object URL → invisible
      // anchor → click → revoke. Has to happen on the same tick as
      // the user gesture for Safari to allow the download.
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success(`${label} exported`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : `Couldn't export ${label.toLowerCase()}`)
    } finally {
      setExporting(null)
    }
  }

  return (
    <Card sx={{ maxWidth: 720 }}>
      <CardHeader title="Export data" subtitle="Download CSV files using your org's date and currency format." />
      <Stack spacing={1.5} sx={{ px: 3.5, pb: 3.5 }}>
        {EXPORT_OPTIONS.map((opt) => {
          const Icon = opt.icon
          const isExp = exporting === opt.label
          return (
            <Stack key={opt.label} direction="row" alignItems="center" spacing={2}
              sx={{ px: 2.5, py: 2, borderRadius: 1.75, border: '1px solid', borderColor: 'divider',
                transition: 'border-color 120ms ease',
                '&:hover': { borderColor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.16)' } }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(15,23,42,0.04)' : 'rgba(255,255,255,0.06)', color: 'text.secondary', flexShrink: 0 }}>
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.005em' }}>{opt.label}</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{opt.desc}</Typography>
              </Box>
              <Button variant="outlined" size="small" disabled={isExp} onClick={() => handleExport(opt.label, opt.resource)}
                startIcon={isExp ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
                sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', borderColor: 'divider', flexShrink: 0, minWidth: 110 }}>
                {isExp ? 'Exporting…' : 'Export CSV'}
              </Button>
            </Stack>
          )
        })}
      </Stack>
    </Card>
  )
}

// ─── Shared building blocks ─────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// SIGNATURES TAB
// ═══════════════════════════════════════════════════════════════════════════

function SignaturesTab() {
  const { user, setUser } = useAuthStore()
  const prefs = (user?.preferences ?? {}) as Record<string, any>
  const signatures: EmailSignature[] = Array.isArray(prefs.signatures) ? prefs.signatures : []

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editBody, setEditBody] = useState('')
  const [creating, setCreating] = useState(false)

  const saveM = useMutation({
    mutationFn: async (updated: EmailSignature[]) => {
      return api.users.updateProfile({ preferences: { ...prefs, signatures: updated } } as any)
    },
    onSuccess: async () => {
      const me = await api.auth.me()
      setUser(me)
      toast.success('Signatures saved')
      setEditingId(null)
      setCreating(false)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  function handleAdd() {
    setCreating(true)
    setEditName('')
    setEditBody('')
  }

  function handleSaveNew() {
    const sig: EmailSignature = { id: crypto.randomUUID(), name: editName.trim(), body: editBody.trim() }
    saveM.mutate([...signatures, sig])
  }

  function handleEdit(sig: EmailSignature) {
    setEditingId(sig.id)
    setEditName(sig.name)
    setEditBody(sig.body)
    setCreating(false)
  }

  function handleSaveEdit() {
    const updated = signatures.map((s) => s.id === editingId ? { ...s, name: editName.trim(), body: editBody.trim() } : s)
    saveM.mutate(updated)
  }

  function handleDelete(id: string) {
    saveM.mutate(signatures.filter((s) => s.id !== id))
  }

  function handleSetDefault(id: string) {
    const updated = signatures.map((s) => ({ ...s, isDefault: s.id === id }))
    saveM.mutate(updated)
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Card>
        <CardHeader title="Email signatures" subtitle="Create signatures to use when sending emails from the app (e.g. reorder requests). Your default signature will be pre-selected." />
        <Box sx={{ px: 3.5, pb: 3.5 }}>
          {signatures.length === 0 && !creating && (
            <Box sx={{ py: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, mb: 2 }}>
              <DrawOutlinedIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>No signatures yet</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>Create your first email signature to attach to outgoing emails.</Typography>
            </Box>
          )}

          <Stack spacing={2}>
            {signatures.map((sig) => (
              editingId === sig.id ? (
                <Box key={sig.id} sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 2, p: 2.5 }}>
                  <TextField label="Signature name" value={editName} onChange={(e) => setEditName(e.target.value)}
                    size="small" fullWidth placeholder="e.g. Work, Formal" sx={{ mb: 2 }} />
                  <TextField label="Signature content" value={editBody} onChange={(e) => setEditBody(e.target.value)}
                    size="small" fullWidth multiline minRows={4}
                    placeholder={'Best regards,\nJohn Doe\nMaintenance Manager\nBacsys Innovations\n+91 99623 03968'} />
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button variant="contained" size="small" disabled={!editName.trim() || !editBody.trim() || saveM.isPending}
                      onClick={handleSaveEdit} sx={{ fontSize: 12.5 }}>
                      {saveM.isPending ? 'Saving...' : 'Save'}
                    </Button>
                    <Button size="small" onClick={() => setEditingId(null)} sx={{ fontSize: 12.5, color: 'text.secondary' }}>Cancel</Button>
                  </Stack>
                </Box>
              ) : (
                <Box key={sig.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.main' } }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{sig.name}</Typography>
                      {sig.isDefault && <Chip label="Default" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: (t) => `${t.palette.primary.main}18`, color: 'primary.main' }} />}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      {!sig.isDefault && (
                        <Tooltip title="Set as default signature" arrow>
                          <IconButton size="small" onClick={() => handleSetDefault(sig.id)} sx={{ color: 'text.secondary' }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit" arrow>
                        <IconButton size="small" onClick={() => handleEdit(sig)} sx={{ color: 'text.secondary' }}>
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton size="small" onClick={() => handleDelete(sig.id)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{sig.body}</Typography>
                </Box>
              )
            ))}

            {creating && (
              <Box sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 2, p: 2.5 }}>
                <TextField label="Signature name" value={editName} onChange={(e) => setEditName(e.target.value)}
                  size="small" fullWidth autoFocus placeholder="e.g. Work, Formal" sx={{ mb: 2 }} />
                <TextField label="Signature content" value={editBody} onChange={(e) => setEditBody(e.target.value)}
                  size="small" fullWidth multiline minRows={4}
                  placeholder={'Best regards,\nJohn Doe\nMaintenance Manager\nBacsys Innovations\n+91 99623 03968'}
                  helperText="This will appear at the bottom of your outgoing emails" />
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button variant="contained" size="small" disabled={!editName.trim() || !editBody.trim() || saveM.isPending}
                    onClick={handleSaveNew} sx={{ fontSize: 12.5 }}>
                    {saveM.isPending ? 'Creating...' : 'Create signature'}
                  </Button>
                  <Button size="small" onClick={() => setCreating(false)} sx={{ fontSize: 12.5, color: 'text.secondary' }}>Cancel</Button>
                </Stack>
              </Box>
            )}
          </Stack>

          {!creating && !editingId && (
            <Button startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={handleAdd}
              sx={{ mt: 2, fontSize: 13, fontWeight: 600, color: 'primary.main' }}>
              Add signature
            </Button>
          )}
        </Box>
      </Card>
    </Box>
  )
}

// ─── Shared helpers ───────────────────────────────────────────────────────

function Card({ children, sx, ...props }: React.ComponentProps<typeof Box>) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', overflow: 'hidden', ...sx }} {...props}>
      {children}
    </Box>
  )
}

function CardHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Box sx={{ px: 3.5, pt: 3, pb: 2.5 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.015em' }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.35 }}>{subtitle}</Typography>}
    </Box>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.85 }}>
        {label}
      </Typography>
      {children}
    </Box>
  )
}

const inputSx = { '& .MuiOutlinedInput-root': { fontSize: 13.5 } }
